import { useState, useEffect } from "react";
import { useApiClient } from "./useApiClient";
import { getTodayTimeRange } from "../utils/dateUtils";
import { apiRoutes } from "../api/routes";

/**
 * Polls the count of unread driver Messages for today, mirroring the legacy
 * `drt_ui` Rails app's chat-bubble badge in the header navbar.
 *
 * In the old app the count was the `message` field of
 * `GET /shared/alert_status/count_xhr` (which itself came from
 * `Alert.summary_query(..., scope: :scope_status_counts)`).
 *
 * The React port queries the same upstream API the Messages page uses
 * (`support/internal/alert/select_query`, type ∈ {11, 12}, today, unread).
 * Polled every 60 s to match the cadence of `useNotificationCount` and
 * `useScheduledRunsCount` so all three header badges refresh together.
 *
 * Errors are reported but do not throw — the previous count is kept so
 * a transient network error doesn't flash "0" in the header.
 */
export const useUnreadMessagesCount = () => {
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  useEffect(() => {
    let isMounted = true;

    const fetchUnreadMessagesCount = async () => {
      try {
        const { start, end } = getTodayTimeRange();
        const requestBody = {
          select: ["id"],
          query: [
            // Same scope as buildMessagesQuery (messagesQueryBuilder.ts:17, 44)
            ["in", "type", [11, 12]],
            // Today range only — mirrors the legacy `sf_range_default_enum: SF_RANGE_TODAY`
            ["between", "timestamp", start, end],
            // Unread only — mirrors the legacy `sf_handle_status: MESSAGE_SF_HANDLE_STATUS_UNREAD`
            // and the React Messages page's `status: "unread"` → `null resolve_timestamp` clause
            ["null", "resolve_timestamp"],
          ],
          options: {
            timeout: 10,
            limit: 0,
            calculate_total: true,
            return_results: false,
            select_distinct: true,
          },
        };

        const response = await apiClient.post(
          apiRoutes.proxy("support/internal/alert/select_query"),
          requestBody,
        );

        if (!isMounted) return;
        if (response.data && response.data.info) {
          setUnreadMessagesCount(response.data.info.total_results ?? 0);
          setError(null);
        } else {
          setError("Failed to fetch unread messages count");
        }
      } catch (err: unknown) {
        if (!isMounted) return;
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
        // Fail silently — keep the previous count.
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchUnreadMessagesCount();
    const interval = setInterval(fetchUnreadMessagesCount, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [apiClient]);

  return { unreadMessagesCount, isLoading, error };
};
